/**
 * AddVeEffortModal — create or edit a Value Engineering effort (Supabase).
 *
 * Opens via CustomEvent('open-add-ve-effort-modal', { detail }):
 *   create from milestone: { projectId, milestoneAirtableId }
 *   edit: { effortId, prefill: { effort_number, status, baseline_estimate } }
 *
 * On create, the effort number auto-computes (max existing + 1). Busts ctx.veEffortCache
 * + ctx.msVeCache and dispatches outline-render on success.
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { createEffort, updateEffort, VE_STATUS_LABELS } from '../services/valueEngineering.js'
import Select from './Select.jsx'

const STATUS_OPTIONS = Object.keys(VE_STATUS_LABELS).map(v => ({ value: v, label: VE_STATUS_LABELS[v] }))

const labelStyle = { display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase' }
const inputStyle = { width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, boxSizing: 'border-box', outline: 'none' }

export default function AddVeEffortModal() {
  const [open, setOpen]             = useState(false)
  const [projectId, setProjectId]   = useState(null)
  const [msAirtableId, setMsId]     = useState(null)
  const [editId, setEditId]         = useState(null)
  const [number, setNumber]         = useState('')
  const [status, setStatus]         = useState('gathering')
  const [baseline, setBaseline]     = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState(null)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      setProjectId(d.projectId || null)
      setMsId(d.milestoneAirtableId || null)
      setEditId(d.effortId || null)
      const p = d.prefill || {}
      let nextNum = p.effort_number != null ? String(p.effort_number) : ''
      if (!d.effortId && nextNum === '') {
        const existing = Array.isArray(ctx.veEffortCache) ? ctx.veEffortCache : []
        nextNum = String((existing.reduce((m, c) => Math.max(m, c.effort_number || 0), 0)) + 1)
      }
      setNumber(nextNum)
      setStatus(p.status || 'gathering')
      setBaseline(p.baseline_estimate != null ? String(p.baseline_estimate) : '')
      setError(null); setSubmitting(false); setOpen(true)
    }
    window.addEventListener('open-add-ve-effort-modal', onOpen)
    return () => window.removeEventListener('open-add-ve-effort-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  async function handleSubmit() {
    setSubmitting(true); setError(null)
    const fields = {
      effort_number: number ? parseInt(number, 10) : null,
      status,
      baseline_estimate: baseline ? Number(baseline) : null,
    }
    try {
      if (editId) {
        await updateEffort(editId, fields)
      } else {
        if (!projectId) { setError('No project in context.'); setSubmitting(false); return }
        await createEffort(projectId, fields, msAirtableId)
        if (msAirtableId && ctx.msVeCache) ctx.msVeCache[msAirtableId] = undefined
      }
      ctx.veEffortCache = undefined
      setOpen(false)
      window.dispatchEvent(new Event('outline-render'))
    } catch (ex) {
      setError(ex.message || 'Failed to save effort'); setSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="AddVeEffortModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 400, padding: '20px 20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>
            {editId ? 'Edit VE Effort' : 'Add VE Effort'}
          </span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ width: 110 }}>
              <label style={labelStyle}>Effort #</label>
              <input type="number" style={inputStyle} value={number} onChange={e => setNumber(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Status</label>
              <Select value={status} onChange={setStatus} options={STATUS_OPTIONS} style={{ width: '100%', fontSize: 12 }} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Baseline Estimate ($)</label>
            <input type="number" style={inputStyle} value={baseline} onChange={e => setBaseline(e.target.value)} placeholder="e.g. 3631407" />
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={submitting} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: submitting ? 'var(--aj-bg-muted)' : '#07272D', color: submitting ? 'var(--aj-text-muted)' : '#fff', border: 'none', borderRadius: 4, cursor: submitting ? 'default' : 'pointer' }}>
              {submitting ? 'Saving…' : (editId ? 'Save' : 'Add Effort')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
